import React, { useState, useEffect } from 'react';
import { Truck, Eye, Search, RefreshCw, Calendar, Package } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { POrdersDto, POrderLineDto } from '../../../../../types/porders';
import { BackButton } from '../../../../common/BackButton';

interface POrdersViewerProps {
    onBack: () => void;
}

export const POrdersViewer: React.FC<POrdersViewerProps> = ({ onBack }) => {
    const [pOrders, setPOrders] = useState<POrdersDto[]>([]);
    const [selectedPOrder, setSelectedPOrder] = useState<POrdersDto | null>(null);
    const [pOrderLines, setPOrderLines] = useState<POrderLineDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadPOrders();
    }, []);

    const loadPOrders = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllPOrders();
            setPOrders(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod ved lasting av ordrer');
            console.error('Error loading orders:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadPOrderLines = async (orderNo: number) => {
        try {
            setLoading(true);
            setError('');
            
            console.log('Loading details for order:', orderNo);
            
            const data = await dataInspectionService.getPOrderWithLines(orderNo);
            console.log('Order data:', data);
            
            setSelectedPOrder(data.header);
            setPOrderLines(data.lines || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod ved lasting av ordredetaljer');
            console.error('Error loading order details:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewPOrder = (pOrder: POrdersDto) => {
        console.log('Viewing order:', pOrder.orderNo);
        loadPOrderLines(pOrder.orderNo);
    };

    const handleBackToList = () => {
        setSelectedPOrder(null);
        setPOrderLines([]);
        setError(''); // Clear any errors when going back
    };

    const filteredPOrders = pOrders.filter(pOrder => 
        pOrder.orderNo.toString().includes(searchTerm) ||
        pOrder.supplierNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pOrder.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pOrder.orderNoERP?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status: number) => {
        switch (status) {
            case 0: return 'bg-gray-100 text-gray-800';
            case 10: return 'bg-blue-100 text-blue-800';
            case 20: return 'bg-yellow-100 text-yellow-800';
            case 30: return 'bg-orange-100 text-orange-800';
            case 40: return 'bg-green-100 text-green-800';
            case 45: return 'bg-green-100 text-green-800';
            case 50: return 'bg-purple-100 text-purple-800';
            case 60: return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case 0: return 'Ubehandlet';
            case 10: return 'Godkjent';
            case 20: return 'Under arbeid';
            case 30: return 'Behandlet, ikke komplett';
            case 40: return 'Behandlet, komplett';
            case 45: return 'Til pakking';
            case 50: return 'Klar for eksport';
            case 60: return 'Eksportert';
            default: return `Status ${status}`;
        }
    };

    // Loading indicator
    if (loading && !selectedPOrder && pOrders.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Laster ordrer...</span>
                </div>
            </div>
        );
    }

    if (selectedPOrder) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <BackButton
                        onClick={handleBackToList}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        iconSize={16}
                        label="Tilbake til liste"
                    />
                    <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-purple-600" />
                        <h1 className="text-xl font-semibold">Innkjøpsordre {selectedPOrder.orderNo}</h1>
                    </div>
                    {loading && (
                        <div className="flex items-center gap-2 text-blue-600">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Laster...</span>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-700">
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            <span className="font-medium">Feil:</span>
                        </div>
                        <p className="text-red-600 mt-1">{error}</p>
                    </div>
                )}

                {/* POrder Header Info */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordre Nr.</label>
                            <div className="text-lg font-semibold text-gray-900">{selectedPOrder.orderNo}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">ERP Nr.</label>
                            <div className="text-gray-900">{selectedPOrder.orderNoERP || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPOrder.orderStatus)}`}>
                                {getStatusText(selectedPOrder.orderStatus)}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Leverandør Nr.</label>
                            <div className="text-gray-900">{selectedPOrder.supplierNo || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Leverandørnavn</label>
                            <div className="text-gray-900">{selectedPOrder.supplierName || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordredato</label>
                            <div className="text-gray-900">
                                {selectedPOrder.orderDate ? new Date(selectedPOrder.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Forventet levering</label>
                            <div className="text-gray-900">
                                {selectedPOrder.deliveryDate ? new Date(selectedPOrder.deliveryDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Lager</label>
                            <div className="text-gray-900">{selectedPOrder.warehouse || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Vår ref.</label>
                            <div className="text-gray-900">{selectedPOrder.ourRef || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {/* POrder Lines */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Orderlinjer ({pOrderLines.length})</h2>
                    </div>
                    {pOrderLines.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            {loading ? 'Laster orderlinjer...' : 'Ingen orderlinjer funnet'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linje</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vare</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bestilt</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mottatt</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enhet</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kostpris</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {pOrderLines.map(line => (
                                        <tr key={line.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {line.lineNo}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {line.itemNo}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {line.description1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {line.quantity}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {line.quantityDelivered || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {line.itemUnitCode}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {line.price ? `${line.price.toFixed(2)} kr` : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                                                    {getStatusText(line.lineStatus)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-purple-600" />
                    <h1 className="text-xl font-semibold">Innkjøpsordrer</h1>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Søk etter ordre nr., leverandør eller ERP nr..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <button
                        onClick={loadPOrders}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Oppdater
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* POrders Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Innkjøpsordrer ({filteredPOrders.length})</h2>
                </div>
                {filteredPOrders.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                        {loading ? 'Laster ordrer...' : 'Ingen ordrer funnet'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordre Nr.</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ERP Nr.</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverandør</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverandørnavn</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordredato</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forventet levering</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredPOrders.map(pOrder => (
                                    <tr key={pOrder.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {pOrder.orderNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pOrder.orderNoERP || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pOrder.orderStatus)}`}>
                                                {getStatusText(pOrder.orderStatus)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pOrder.supplierNo || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {pOrder.supplierName || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pOrder.orderDate ? new Date(pOrder.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pOrder.deliveryDate ? new Date(pOrder.deliveryDate).toLocaleDateString('nb-NO') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleViewPOrder(pOrder)}
                                                disabled={loading}
                                                className="flex items-center gap-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Vis detaljer
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
